import { Alert } from "@mui/material";

import { type SessionFile } from "@/shared/domain/sessions";
import { useSessionFiles } from "@/shared/domain/sessions/hooks";
import {
  buildColumnsForPreferences,
  colId,
  createColumnHelper,
  DataTable,
  deriveLoadingState,
  useDataTableState,
  type ColDef,
  type ValueFormatterParams,
} from "@/shared/foundation/primitives/Table";
import { userFacingErrorMessage } from "@/shared/http";
import { formatNanoTimestamp } from "@/shared/time";

import styles from "./SourcesTab.module.css";
import { useSessionOutletContext } from "./useSessionOutletContext";

const columnHelper = createColumnHelper<SessionFile>();

function formatRangeBound(
  params: ValueFormatterParams<SessionFile, bigint | null>,
): string {
  // An absent bound means the contribution covers the whole file; render
  // a dash so the column reads as "open" rather than missing data. AG
  // Grid widens `value` to `TValue | null | undefined`, so collapse both.
  if (params.value === null || params.value === undefined) {
    return "—";
  }
  return formatNanoTimestamp(params.value);
}

const COLUMNS: ColDef<SessionFile>[] = [
  columnHelper.accessor("file_id", {
    headerName: "File ID",
    flex: 2,
    cellClass: styles.monoCell,
  }),
  columnHelper.accessor("range_min_timestamp_ns", {
    headerName: "Range start",
    flex: 1,
    cellClass: styles.monoCell,
    valueFormatter: formatRangeBound,
  }),
  columnHelper.accessor("range_max_timestamp_ns", {
    headerName: "Range end",
    flex: 1,
    cellClass: styles.monoCell,
    valueFormatter: formatRangeBound,
  }),
];

const DEFAULT_VISIBLE_IDS = COLUMNS.map(colId);

const PREFERENCE_DRIVEN_COLUMNS = buildColumnsForPreferences<SessionFile>({
  staticColumns: COLUMNS,
  dynamicColumns: [],
  preferenceOrder: DEFAULT_VISIBLE_IDS,
});

/**
 * Sources tab of the session-detail page. Lists the per-file
 * contributions to this session, one page at a time, with no filters or
 * sort.
 */
export function SourcesTab() {
  const { session } = useSessionOutletContext();
  const query = useSessionFiles({ sessionId: session.session_id });

  const tableState = useDataTableState<SessionFile>({
    getRowId: (row) => row.file_id,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_IDS,
  });

  if (query.isError) {
    return (
      <Alert severity="error">
        {userFacingErrorMessage(
          query.error,
          <>
            Failed to load files. Please contact{" "}
            <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
            problem persists.
          </>,
        )}
      </Alert>
    );
  }

  return (
    <DataTable.Root>
      <DataTable.Table<SessionFile>
        {...tableState.table}
        columns={PREFERENCE_DRIVEN_COLUMNS}
        data={query.data?.items ?? []}
        hasNextPage={Boolean(query.data?.next_token)}
        loading={deriveLoadingState(query)}
        noRowsContent="No files contribute to this session yet"
      />
    </DataTable.Root>
  );
}
